/**
 * skyAnchor — keeps sky bodies (the sun/moon and their sky bloom) at a fixed
 * spot in the camera view.
 *
 * A normalized anchor (`atX`/`atY`) resolves through the scatter region, which
 * is built from the camera AT MOUNT. Placed once, a moon became a world fixture:
 * it slid across the screen at full world speed as the player walked, rose and
 * fell with every jump, left the screen, and jumped back when the scene
 * re-mounted around the new camera. DJ (2026-10-08, First Field, plan 23 row 52):
 * "Some lighting moves around and rotates weird, like this moon one above my
 * character". A sky body is infinitely far away: it holds its place in the view.
 *
 * Each tick re-resolves the anchor through the region of the LIVE camera
 * (`regionFromCamera`, the same mapping the mount used), so the body sits where
 * it was authored and where it first appeared, and only moves when the camera
 * does. Static: no animation of its own, so reduce-motion needs nothing.
 */
import { lerp } from '../../../shared/utils/MathUtils';
import { regionFromCamera } from './atmospherePlacement';
import type { BuildCtx } from './effectBuilders';
import type { ISprite } from '../../rendering/IRenderer';

/** Normalized glow anchors at or above this `atY` are sky light (sun/moon bloom),
 *  not floor or canopy glow, and follow the view with the body they surround. */
export const SKY_BAND_MAX_AT_Y = 0.3;

export interface SkyAnchored {
  sprite: ISprite;
  atX: number;
  atY: number;
}

/** Re-anchor `items` to the live camera every tick. Returns the unsubscribe. */
export function anchorToSky(ctx: Pick<BuildCtx, 'addTick' | 'getCameraView'>, items: readonly SkyAnchored[]): () => void {
  if (!items.length) return () => {};
  let lastX = Number.NaN;
  let lastY = Number.NaN;
  let lastW = Number.NaN;
  let lastH = Number.NaN;
  const place = () => {
    const v = ctx.getCameraView();
    if (!v) return;
    if (v.centerX === lastX && v.centerY === lastY && v.width === lastW && v.height === lastH) return;
    lastX = v.centerX; lastY = v.centerY; lastW = v.width; lastH = v.height;
    const r = regionFromCamera(v);
    for (const it of items) it.sprite.setPosition(lerp(r.x0, r.x1, it.atX), lerp(r.y0, r.y1, it.atY));
  };
  place();
  return ctx.addTick(place);
}
